import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Handles native app lifecycle: splash screen, status bar, back button, deep links.
 * Call once in App root.
 */
export function useNativeApp() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const cleanup: (() => void)[] = [];

    (async () => {
      // Hide splash screen
      try {
        const mod = await import('@capacitor/splash-screen' as string);
        await mod.SplashScreen.hide();
      } catch { /* not available */ }

      // Set status bar style
      try {
        const mod = await import('@capacitor/status-bar' as string);
        await mod.StatusBar.setStyle({ style: mod.Style.Dark });
        await mod.StatusBar.setBackgroundColor({ color: '#212529' });
      } catch { /* not available */ }

      // Handle hardware back button (Android)
      try {
        const mod = await import('@capacitor/app' as string);
        const listener = await mod.App.addListener('backButton', (ev: { canGoBack: boolean }) => {
          if (ev.canGoBack) {
            window.history.back();
          } else {
            mod.App.minimizeApp();
          }
        });
        cleanup.push(() => listener.remove());

        // Handle deep links
        const urlListener = await mod.App.addListener('appUrlOpen', (ev: { url: string }) => {
          const path = new URL(ev.url).pathname;
          if (path) window.location.href = path;
        });
        cleanup.push(() => urlListener.remove());
      } catch { /* not available */ }

      // Handle keyboard on iOS/Android
      try {
        const mod = await import('@capacitor/keyboard' as string);
        const showListener = await mod.Keyboard.addListener('keyboardWillShow', (info: { keyboardHeight: number }) => {
          document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
        });
        const hideListener = await mod.Keyboard.addListener('keyboardWillHide', () => {
          document.documentElement.style.setProperty('--keyboard-height', '0px');
        });
        cleanup.push(() => showListener.remove());
        cleanup.push(() => hideListener.remove());
      } catch { /* not available */ }
    })();

    return () => cleanup.forEach((fn) => fn());
  }, []);
}
